import type { ReactNode } from 'react';
import React, { createContext, useEffect, useMemo } from 'react';
import { useParams } from 'react-router';

import { setSentryUser } from 'src/modules/app/components/Providers/Telemetry/Sentry';
import { FeatureTracking } from 'src/shared/constants/FeatureFlag';
import {
  useGetRenderingsQuery,
  useProfileQuery,
} from 'src/shared/graphql/generated';
import type { Job, OrgBasicInfo, User } from 'src/shared/graphql/generated';
import { useSplit } from 'src/shared/hooks';
import { identify } from 'src/shared/telemetry';

import { sanitizeImages, sanitizeRenderings } from '../helpers';
import type { RenderingContext } from '../types';
import { initialContext } from '../types';

const context = createContext<RenderingContext>(initialContext);

const Provider = ({ children }: { children: ReactNode }) => {
  // Provider used for the Renderings Hub authenticated view
  const { jobId: jobIdParam } = useParams<{ jobId: string }>();
  const { data: userData } = useProfileQuery();
  const { data, error, loading } = useGetRenderingsQuery({
    variables: { id: parseInt(jobIdParam, 10) },
  });

  const { user } = userData?.profile || {};
  const jobResults = data?.jobs.results;

  const noRenderingFound = !loading && jobResults?.length === 0;
  const otherError = !loading && !!error;

  const job = useMemo(() => (jobResults?.[0] || {}) as Job, [jobResults]);

  const {
    captureRequest,
    name,
    locationLine1,
    hiDefRenderings,
    images,
    orgId,
    id,
  } = job;
  const { orgBrandLogoUrl, customerDisplayName, partnerId } =
    (captureRequest?.displayRequestingOrg || {}) as OrgBasicInfo;

  const value = useMemo(() => {
    const trackingData = {
      feature: FeatureTracking.RENDERING,
      org_id: orgId ?? undefined,
      job_id: id,
      rendering_id: hiDefRenderings?.length ? hiDefRenderings[0].id : undefined,
      partner_id: partnerId ?? undefined,
    };

    return {
      address: locationLine1 || '',
      images: sanitizeImages(images || []),
      jobName: name || '',
      renderings: sanitizeRenderings(hiDefRenderings || []),
      job,
      jobId: id || undefined,
      orgId: orgId || undefined,
      orgName: customerDisplayName || '',
      orgLogoUrl: orgBrandLogoUrl || '',
      isCaptureRequest: !!captureRequest,
      trackingData,
      error: noRenderingFound || otherError,
    };
  }, [
    captureRequest,
    customerDisplayName,
    hiDefRenderings,
    images,
    locationLine1,
    name,
    orgBrandLogoUrl,
    orgId,
    id,
    partnerId,
    job,
    noRenderingFound,
    otherError,
  ]);

  const {
    updateUserId: updateSplitUserId,
    updateAttributes: updateSplitAttributes,
  } = useSplit();

  useEffect(() => {
    if (user) {
      setSentryUser(user.id, user.email);
      updateSplitUserId(user.id?.toString());
      updateSplitAttributes(user as User | undefined);
      void identify({ userId: user.id, email: user.email });
    }
  }, [user, updateSplitUserId, updateSplitAttributes]);

  return <context.Provider value={value}>{children}</context.Provider>;
};

export { Provider, context };
